import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { AppError } from '@st/contract';
import { AuthCard } from '../../components/AuthLayout';
import { HOME_BY_ROLE, useAuth } from '../../auth/AuthProvider';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { Button, Checkbox, Input } from '../../ui';

export function Register() {
  const { t, locale } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    passwordConfirm: '',
    terms: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEmailError(null);
    setFieldError(null);
    setTermsError(null);
    if (form.password !== form.passwordConfirm) {
      setFieldError(t('auth.passwordMismatch'));
      return;
    }
    /*
      Uvjeti su se prije provjeravali gašenjem gumba. Ugašen gumb ne prima ni
      klik ni fokus, pa je registracija za nekoga tko previdi kvačicu izgledala
      kao pokvaren ekran. Sada se šalje, a poruka stoji uz samu kvačicu.
    */
    if (!form.terms) {
      setTermsError(t('auth.termsRequired'));
      return;
    }
    setBusy(true);
    try {
      const user = await register({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        ...(form.lastName ? { lastName: form.lastName } : {}),
        locale,
      });
      navigate(user.onboardingCompleted ? HOME_BY_ROLE[user.role] : '/onboarding', {
        replace: true,
      });
    } catch (err) {
      /*
        Jedini sukob koji registracija može proizvesti jest zauzeta adresa.
        Prije je ovdje stajala općenita poruka („Podaci su se u međuvremenu
        promijenili"), koja na ovom ekranu ne znači ništa — netko tko se već
        registrirao nije imao pojma zašto ne može dalje. Poruka sada stoji uz
        samo polje s adresom i kaže što učiniti.
      */
      if (err instanceof AppError && err.code === 'conflict') {
        setEmailError(t('auth.emailTaken'));
        document.getElementById('reg-email')?.focus();
      } else {
        setError(toErrorMessage(t, err).title);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title={t('auth.registerTitle')}
      subtitle={t('auth.registerSubtitle')}
      footer={
        <p className="text-ink-muted">
          {t('auth.hasAccount')}{' '}
          <Link to="/prijava" className="text-accent-dark underline">
            {t('auth.login')}
          </Link>
        </p>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            id="reg-first"
            label={t('auth.firstName')}
            autoComplete="given-name"
            required
            value={form.firstName}
            onChange={(e) => set('firstName', e.target.value)}
          />
          <Input
            id="reg-last"
            label={t('auth.lastName')}
            autoComplete="family-name"
            value={form.lastName}
            onChange={(e) => set('lastName', e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Input
            id="reg-email"
            type="email"
            label={t('auth.email')}
            autoComplete="email"
            required
            error={emailError ?? undefined}
            value={form.email}
            onChange={(e) => {
              set('email', e.target.value);
              if (emailError) setEmailError(null);
            }}
          />
          {emailError && (
            <p className="text-[13px] text-ink-muted">
              <Link to="/prijava" className="text-accent-dark underline">
                {t('auth.login')}
              </Link>{' '}
              ·{' '}
              <Link to="/zaboravljena-lozinka" className="text-accent-dark underline">
                {t('auth.forgotTitle')}
              </Link>
            </p>
          )}
        </div>
        <Input
          id="reg-password"
          type="password"
          label={t('auth.password')}
          hint={t('auth.passwordHint')}
          autoComplete="new-password"
          minLength={10}
          required
          value={form.password}
          onChange={(e) => set('password', e.target.value)}
        />
        <Input
          id="reg-password2"
          type="password"
          label={t('auth.passwordConfirm')}
          autoComplete="new-password"
          required
          error={fieldError ?? undefined}
          value={form.passwordConfirm}
          onChange={(e) => set('passwordConfirm', e.target.value)}
        />
        <Checkbox
          id="reg-terms"
          label={t('auth.acceptTerms')}
          required
          error={termsError ?? undefined}
          checked={form.terms}
          onChange={(e) => {
            set('terms', e.target.checked);
            if (e.target.checked) setTermsError(null);
          }}
        />
        {error && (
          <p role="alert" className="text-[13px] text-danger">
            {error}
          </p>
        )}
        <Button type="submit" block loading={busy}>
          {t('auth.submitRegister')}
        </Button>
      </form>
    </AuthCard>
  );
}
